import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { ViewMode, FormText, SelectBoxText, FormGroup, FormBuilder } from "@coso/forms";
import { IEmailProfileHolder, SmtpType } from "@coso-replicator/core";
import { Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EmailProfileForm } from "../../../configuration/connections/sharepoint/models/EmailProfileForm";

@Component({
  standalone:false,
  templateUrl: "./add-edit-email-notification-profile.component.html"
})
export class AddEditEmailNotificationProfileComponent implements OnInit {
  private fb = inject(FormBuilder);
  public activeModal = inject(NgbActiveModal);
  ViewMode = ViewMode;
  /** Initial state properties */
  viewMode: ViewMode;
  entity: IEmailProfileHolder;
  errorMessage: string;
  successMessage: string;
  SmtpType = SmtpType;
  private destroyRef = inject(DestroyRef);
  saveCallback: (entity: IEmailProfileHolder) => void;
  sendTestEmailCallback: (entity: IEmailProfileHolder) => void;

  formText: FormText<EmailProfileForm>;
  formGroup: FormGroup<EmailProfileForm>;

  constructor() {}

  get smtpTypeSelectBox() : SelectBoxText {
    return this.formText.smtpType as SelectBoxText;
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group<any>({
      name: this.fb.control<string>(this.entity ? this.entity.name : undefined, [Validators.required]),
      username: this.fb.control<string>(this.entity ? this.entity.userName : undefined, [Validators.required]),
      password: this.fb.control<string>(undefined, [Validators.required]),
      server: this.fb.control<string>(this.entity ? this.entity.smtpServer : undefined, [Validators.required]),
      port: this.fb.control<number>(this.entity ? this.entity.port : 25, [Validators.required, Validators.min(1), Validators.max(65535)]),
      useSsl: this.fb.control<boolean>(this.entity ? this.entity.useSsl : false),
      email: this.fb.control<string>(undefined, [Validators.email]),
      emails: this.fb.array((this.entity && this.entity.recipientEmailAddressText)
        ? this.entity.recipientEmailAddressText.split(",").map(email => this.fb.control<string>(email))
        : [], [Validators.required]),
      fromEmail: this.fb.control<string>(this.entity ? this.entity.fromEmailAddress : undefined, [Validators.email]),
      fromEmailDisplayName: this.fb.control<string>(this.entity ? this.entity.fromEmailDisplayName : undefined),
      smtpType: this.fb.control<number>(this.entity ? this.entity.smtpType : 0, [Validators.required]),
      azureClientId: this.fb.control<string>(this.entity ? this.entity.azureClientId : undefined),
      azureTenantId: this.fb.control<string>(this.entity ? this.entity.azureTenantId : undefined),
      azureClientSecret: this.fb.control<string>(undefined),
      jsonKey: this.fb.control<string>(undefined),
      scopes: this.fb.control<string>(this.entity ? this.entity.scopes : undefined)
    });
    this.formText = {
      name: {
        label: "Name",
        placeholder: "name",
        errors: {
          required: "The Name field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The name is just a field which appears later in the list of profiles. By giving the connection a meaningful name, it is easier to find it later in the list."
      },
      username: {
        label: "From Username",
        placeholder: "username",
        errors: {
          required: "The Username field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The SMTP server username with 'Send As' privilege that will be used to send email notifications."
      },
      smtpType: {
        label: "SMTP Type",
        labelClass: "col-lg-3",
        options: Object.values(SmtpType)
          .filter((v) => isNaN(Number(v)))
          .map((v) => ({ label: v.toString(), value: Number(SmtpType[v]) })),
      },
      password: {
        label: "From Password",
        placeholder: "password",
        errors: {
          required: "The Password field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The SMTP server password of the user."
      },
      server: {
        label: "SMTP Server",
        placeholder: "server",
        errors: {
          required: "The SMTP server field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The address of the SMTP server that will be used for sending the notification emails."
      },
      port: {
        label: "Port",
        errors: {
          required: "The Port field is required",
          min: "The Port field must be greater than or equal to 1",
          max: "The Port field must be less than or equal to 65535"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The SMTP server port. Port 25 is the default SMTP port."
      },
      useSsl: {
        label: "Use SSL",
        labelClass: "col-lg-3",
        disable: "Off",
        enable: "On",
        popover: "Enables the secure sockets layer (SSL)."
      },
      email: {
        label: "Recipient Email(s)",
        placeholder: "recipient email address",
        errors: {
          required: "Please enter at least one email address and click Enter",
          email: "Invalid email address. Please make sure it looks like this: email@domain.com"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The recipients email list.",
        help: "Enter an email address and click Enter"
      },
      fromEmail: {
        label: "From Email",
        placeholder: "sender's email address",
        errors: {
          email: "The From Email field is invalid or malformed"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The sender's email address. Helps to identify the sender of the email to the recipients."
      },
      fromEmailDisplayName: {
        label: "From Name",
        placeholder: "sender's display name",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The sender's email display name. Helps to identify the sender of the email to the recipients."
      },
      azureClientId: {
        label: "Azure Client ID",
        placeholder: "client id",
        errors: {
          required: "The Client ID field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The Azure Client ID for authentication."
      },
      azureTenantId: {
        label: "Azure Tenant ID",
        placeholder: "tenant id",
        errors: {
          required: "The Tenant ID field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The Azure Tenant ID for authentication."
      },
      azureClientSecret: {
        label: "Azure Client Secret",
        placeholder: "client secret",
        errors: {
          required: "The Client Secret field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The Azure Client Secret for authentication."
      },
      jsonKey: {
        label: "JSON Key",
        placeholder: "json key",
        errors: {
          required: "The JSON Key field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The JSON Key for authentication."
      },
      scopes: {
        label: "Scopes",
        placeholder: "scopes",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7",
        popover: "The scopes for authentication."
      }
    };
    this.setUpSmtpType();
  }

  setUpSmtpType(){
    this.formGroup.controls.smtpType.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((smtpType) => {
      switch(smtpType){
        case SmtpType.MicrosoftAuthentication:{
          this.formGroup.controls.azureClientId.setValidators([Validators.required]);
          this.formGroup.controls.azureTenantId.setValidators([Validators.required]);
          this.formGroup.controls.azureClientSecret.setValidators([Validators.required]);
          this.formGroup.controls.jsonKey.clearValidators();
          this.formGroup.controls.password.clearValidators();
          this.formGroup.controls.scopes.setValidators([Validators.required]);
          this.formGroup.controls.scopes.setValue("https://outlook.office365.com/.default");
          break;
        }
        case SmtpType.GoogleAuthentication:{
          this.formGroup.controls.azureClientId.clearValidators();
          this.formGroup.controls.azureTenantId.clearValidators();
          this.formGroup.controls.azureClientSecret.clearValidators();
          this.formGroup.controls.jsonKey.setValidators([Validators.required]);
          this.formGroup.controls.password.clearValidators();
          this.formGroup.controls.scopes.setValidators([Validators.required]);
          this.formGroup.controls.scopes.setValue("https://mail.google.com/");
          break;
        }
        default: {
          this.formGroup.controls.azureClientId.clearValidators();
          this.formGroup.controls.azureTenantId.clearValidators();
          this.formGroup.controls.azureClientSecret.clearValidators();
          this.formGroup.controls.jsonKey.clearValidators();
          this.formGroup.controls.password.setValidators([Validators.required]);
          this.formGroup.controls.scopes.clearValidators();
          this.formGroup.controls.scopes.setValue(undefined);
          break;
        }
      }
      this.formGroup.controls.jsonKey.updateValueAndValidity();
      this.formGroup.controls.azureTenantId.updateValueAndValidity();
      this.formGroup.controls.azureClientId.updateValueAndValidity();
      this.formGroup.controls.azureClientSecret.updateValueAndValidity();
      this.formGroup.controls.password.updateValueAndValidity();
      this.formGroup.controls.scopes.updateValueAndValidity();
      this.formGroup.updateValueAndValidity();
      this.formGroup.markAsPristine();
    });
  }

  sendTestEmail() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
    } else {
      const holder : IEmailProfileHolder = {
        name: this.formGroup.controls.name.value,
        userName: this.formGroup.controls.username.value,
        password: this.formGroup.controls.password.value,
        smtpServer: this.formGroup.controls.server.value,
        port: this.formGroup.controls.port.value,
        useSsl: this.formGroup.controls.useSsl.value,
        recipientEmailAddressText: this.formGroup.controls.emails.value.join(",").trim(),
        fromEmailAddress: this.formGroup.controls.fromEmail.value,
        fromEmailDisplayName: this.formGroup.controls.fromEmailDisplayName.value,
        isCurrentlyInUse: false,
        isUsedInConfiguration: false,
        passwordHash: undefined,
        tenantId: undefined,
        scopes: this.formGroup.controls.scopes.value,
        azureClientSecret: this.formGroup.controls.azureClientSecret.value,
        azureClientId: this.formGroup.controls.azureClientId.value,
        azureTenantId: this.formGroup.controls.azureTenantId.value,
        jsonKey: this.formGroup.controls.jsonKey.value,
        smtpType: this.formGroup.controls.smtpType.value
      };
      this.sendTestEmailCallback(holder);
    }
  }

  save() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
    } else {
      const holder : IEmailProfileHolder = {
        name: this.formGroup.controls.name.value,
        userName: this.formGroup.controls.username.value,
        password: this.formGroup.controls.password.value,
        smtpServer: this.formGroup.controls.server.value,
        port: this.formGroup.controls.port.value,
        useSsl: this.formGroup.controls.useSsl.value,
        recipientEmailAddressText: this.formGroup.controls.emails.value.join(",").trim(),
        fromEmailAddress: this.formGroup.controls.fromEmail.value,
        fromEmailDisplayName: this.formGroup.controls.fromEmailDisplayName.value,
        isCurrentlyInUse: false,
        isUsedInConfiguration: false,
        passwordHash: undefined,
        tenantId: undefined,
        scopes: this.formGroup.controls.scopes.value,
        azureClientSecret: this.formGroup.controls.azureClientSecret.value,
        azureClientId: this.formGroup.controls.azureClientId.value,
        azureTenantId: this.formGroup.controls.azureTenantId.value,
        jsonKey: this.formGroup.controls.jsonKey.value,
        smtpType: this.formGroup.controls.smtpType.value
      };
      this.saveCallback(holder);
    }
  }
}
